"use client";

import { useEffect, useRef, useState } from "react";
import type { PostHog } from "posthog-js";
import { usePathname } from "next/navigation";
import { isEmbedPreviewDocument } from "@/lib/embed-preview";
import { isProductionHost } from "@/config/domains";

function PostHogPageView({
  client,
  enabled,
}: {
  client: PostHog | null;
  enabled: boolean;
}) {
  const pathname = usePathname();
  const pendingPageviews = useRef<
    Array<{ key: string; properties: Parameters<PostHog["capture"]>[1] }>
  >([]);

  useEffect(() => {
    if (!enabled) {
      pendingPageviews.current = [];
      return;
    }
    if (!pathname) return;
    // Query params are read from the window, not useSearchParams(): the hook
    // errored during prerender of static public pages (empty-digest Suspense
    // boundary in the SSR HTML), which failed React hydration (#418) with the
    // full tree regenerated on the client. Pageviews only fire on the client,
    // so window.location.search is equivalent here.
    const searchParams = new URLSearchParams(window.location.search);
    const isDashboard = pathname.startsWith("/dashboard") || pathname.startsWith("/admin");
    const safeParams = new URLSearchParams();
    if (isDashboard) {
      for (const key of ["tab", "form"] as const) {
        const value = searchParams.get(key);
        if (value) safeParams.set(key, value);
      }
    } else {
      for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const) {
        const value = searchParams.get(key);
        if (value) safeParams.set(key, value);
      }
    }
    const url = safeParams.size > 0 ? `${pathname}?${safeParams.toString()}` : pathname;
    const properties = {
      $current_url: url,
      ...(isDashboard
        ? {
            client_id: searchParams.get("client_id") ?? undefined,
            admin_view: searchParams.get("admin_view") === "1",
          }
        : {}),
    };
    const key = JSON.stringify(properties);

    if (!client) {
      pendingPageviews.current.push({ key, properties });
      return;
    }

    const pageviews = pendingPageviews.current.splice(0);
    if (pageviews[pageviews.length - 1]?.key !== key) {
      pageviews.push({ key, properties });
    }
    for (const pageview of pageviews) {
      client.capture("$pageview", pageview.properties);
    }
  }, [pathname, client, enabled]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [client, setClient] = useState<PostHog | null>(null);
  const [trackingEnabled, setTrackingEnabled] = useState(false);

  useEffect(() => {
    if (!isProductionHost(window.location.hostname)) return;

    const key =
      process.env.NEXT_PUBLIC_POSTHOG_KEY ??
      process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;

    if (!key) return;

    // The landing hero's dashboard iframe shares this root layout. Initialising
    // PostHog there fires a fake /dashboard/leads pageview on every landing visit
    // and adds ~3s of /ingest requests to a frame nobody can interact with.
    if (isEmbedPreviewDocument()) return;

    let cancelled = false;
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const initialize = () => {
      if (cancelled) return;
      setTrackingEnabled(true);

      void import("posthog-js")
        .then(({ default: posthog }) => {
          if (cancelled) return;

          const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
          if (!posthog.__loaded) {
            posthog.init(key, {
              api_host: "/ingest",
              ui_host: host,
              defaults: "2026-01-30",
              person_profiles: "identified_only",
              // Explicit events and pageviews are enough for the public funnel. Avoid
              // autocapturing form interactions or recording pages that can contain PII.
              autocapture: false,
              disable_session_recording: true,
              capture_pageview: false,
              capture_pageleave: true,
              capture_exceptions: false,
              // The funnel uses explicit events only. These flags stop PostHog from
              // fetching surveys.js, dead-clicks-autocapture.js and web-vitals scripts
              // from /ingest (~50 KB plus their main-thread tasks on every page view).
              disable_surveys: true,
              disable_product_tours: true,
              capture_dead_clicks: false,
              capture_performance: false,
            });
          }
          setClient(posthog);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setTrackingEnabled(false);
          console.error("[PostHog] Failed to load analytics", error);
        });
    };

    if ("requestIdleCallback" in window) {
      idleHandle = window.requestIdleCallback(initialize, { timeout: 1500 });
    } else {
      timeoutHandle = setTimeout(initialize, 500);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, []);

  return (
    <>
      <PostHogPageView client={client} enabled={trackingEnabled} />
      {children}
    </>
  );
}
